import {apiGet, apiPost, apiPut, buildPaginatedUrl} from "../utils/api.util";
import {ApiResponse} from "../models/api-response.model";
import {ApprovableActionRequest, ApprovableShortResponse} from "../models/approvable.model";
import {ApprovableStatus} from "../models/approvable-status.model";
import {PaginationParams} from "../models/pagination.model";

export interface LabourPayment {
    id?: number,
    projectId: number,
    items: LabourPaymentItem[],
    status?: ApprovableStatus
}

export interface LabourPaymentItem {
    id?: number,
    serialNumber: number,
    projectLabourId: number,
    amount: number
    remark: string
}

export interface LabourPaymentResponse {
    id: number,
    companyId: number,
    companyName: string,
    aboutCompany: string,
    referenceNumber: string,
    projectId: number,
    projectName: string,
    createdAt: string,
    preparedById: number,
    preparedByName: string,
    paidById: number | null,
    paidByName: string | null,
    preparedBySignature: string,
    paidBySignature: string | null,
    amount: number,
    approvedAmount: number,
    approvedAt: string | null,
    approvedById: number | null,
    approvedByName: string | null,
    approvedBySignature: string | null,
    rejectedById: number | null,
    rejectedByName: string | null,
    rejectedBySignature: string | null,
    paidAt: string | null,
    rejectedAt: string | null,
    receiptNumber: string | null,
    receipt: string | null, // Base64 encoded byte array
    description:string
    status: ApprovableStatus,
    items: LabourPaymentItemResponse[]
}

export interface LabourPaymentItemResponse {
    id: number
    serialNumber: number,
    labourPaymentId: number,
    projectLabourId: number,
    labourName: string,
    labourOccupation: string,
    labourContractAmount: number,
    amount: number,
    labourPhone: string,
    totalPaid: number,
    totalBalance: number,
    remark: string
}

export const createLabourPayment = async (labourPayment: LabourPayment): Promise<ApiResponse<ApprovableShortResponse>> => {
    const response = await apiPost('/api/v1/pm/labour-payments', labourPayment);
    return await response.json();
}

export const updateLabourPayment = async (id: number, labourPayment: LabourPayment): Promise<ApiResponse<ApprovableShortResponse>> => {
    const response = await apiPut(`/api/v1/pm/labour-payments/${id}`, labourPayment);
    return await response.json();
}

export const getLabourPaymentById = async (id: number): Promise<ApiResponse<LabourPaymentResponse>> => {
    const response = await apiGet(`/api/v1/pm/labour-payments/${id}`);
    return await response.json();
}

export const getLabourPaymentsForEmployee = async (employeeId: number, projectId?: number, params?: PaginationParams): Promise<ApiResponse<LabourPaymentResponse[]>> => {
    const baseUrl = projectId
        ? `/api/v1/pm/labour-payments/for-employee/${employeeId}?projectId=${projectId}`
        : `/api/v1/pm/labour-payments/for-employee/${employeeId}`;

    const response = await apiGet(buildPaginatedUrl(baseUrl, params));
    return await response.json();
};

export const printLabourPayment = async (labourPaymentId: number): Promise<Blob> => {
    const response = await apiGet(`/api/v1/pm/labour-payments/${labourPaymentId}/print`);
    return await response.blob();
};

export const previewLabourPayment = async (labourPaymentId: number): Promise<Blob> => {
    const response = await apiGet(`/api/v1/pm/labour-payments/${labourPaymentId}/preview`);
    return await response.blob();
}

export const loadPendingLabourPayments = async (params?: PaginationParams): Promise<ApiResponse<LabourPaymentResponse[]>> => {
    const response = await apiGet(buildPaginatedUrl(`/api/v1/pm/labour-payments/pending`, params));
    return await response.json();
};

export const loadLabourPaymentsForCurrentCompany = async (params?: PaginationParams): Promise<ApiResponse<LabourPaymentResponse[]>> => {
    const response = await apiGet(buildPaginatedUrl(`/api/v1/pm/labour-payments/for-current-company`, params));
    return await response.json();
};

export const applyActionToLabourPayments = async (actionRequest: ApprovableActionRequest): Promise<ApiResponse<LabourPaymentResponse[]>> => {
    const response = await apiPut('/api/v1/pm/labour-payments/apply-action', actionRequest, {});
    return await response.json();
}

export const submitLabourPaymentsForApproval = async (labourPaymentIds: number[]): Promise<ApiResponse<ApprovableShortResponse[]>> => {
    const response = await apiPost('/api/v1/pm/labour-payments/submit-for-approval', labourPaymentIds, {});
    return await response.json();
}
